import React, { FC, useState, useEffect } from 'react';
import { Pagination as UdirPagination, usePagination } from '@udir-design/react/beta';
import { IPaginationProps } from './IPaginationProps';
import { DisplayFormat } from '../../../Models/DisplayFormat';
import './Pagination.scss';

const Pagination: FC<IPaginationProps> = (props: IPaginationProps) => {
    // Use matchMedia for reliable device detection (works with DevTools)
    const [displayFormat, setDisplayFormat] = useState<DisplayFormat>(() => {
        if (window.matchMedia('(max-width: 767px)').matches) return DisplayFormat.Mobile;
        if (window.matchMedia('(max-width: 1023px)').matches) return DisplayFormat.Tablet;
        return DisplayFormat.Desktop;
    });

    useEffect(() => {
        const mobileQuery = window.matchMedia('(max-width: 767px)');
        const tabletQuery = window.matchMedia('(max-width: 1023px)');
        
        const handleChange = () => {
            if (mobileQuery.matches) {
                setDisplayFormat(DisplayFormat.Mobile);
            } else if (tabletQuery.matches) {
                setDisplayFormat(DisplayFormat.Tablet);
            } else {
                setDisplayFormat(DisplayFormat.Desktop);
            }
        };
        
        mobileQuery.addEventListener('change', handleChange);
        tabletQuery.addEventListener('change', handleChange);
        
        return () => {
            mobileQuery.removeEventListener('change', handleChange);
            tabletQuery.removeEventListener('change', handleChange);
        };
    }, []);

    const isMobile = displayFormat === DisplayFormat.Mobile;
    
    const { pages, nextButtonProps, prevButtonProps } = usePagination({
        currentPage: props.currentPage,
        totalPages: props.numberOfPages,
        setCurrentPage: props.onPageChanged,
        showPages: 7,
    });

    // Filter pages for mobile: always show exactly 3 page numbers
    const displayPages = isMobile && props.numberOfPages > 3
        ? pages.filter(({ page }) => {
            if (typeof page === 'number') {
                const current = props.currentPage;
                const total = props.numberOfPages;
                
                // Always show: first (1), current, last (total)
                if (page === 1 || page === total) return true;
                
                // Show current page
                if (page === current) return true;
                
                // If current is 1, show page 2
                if (current === 1 && page === 2) return true;
                
                // If current is last, show page total-1
                if (current === total && page === total - 1) return true;
                
                return false;
            }
            return page === '...'; // Keep ellipsis
          }).filter((item, index, arr) => {
            // Remove consecutive duplicates
            if (index === 0) return true;
            return item.page !== arr[index - 1].page;
          })
        : pages;
    
        console.log(displayPages);

    return (
        <UdirPagination className='sok-pagination' aria-label={props.searchQuery ? `Flere resultater for ${props.searchQuery}` : 'Paginering'}>
            <UdirPagination.List className='sok-pagination-list'>
                <UdirPagination.Item>
                    <UdirPagination.Button {...prevButtonProps}>
                        {!isMobile && 'Forrige'}
                    </UdirPagination.Button>
                </UdirPagination.Item>
                {displayPages.map(({ page, itemKey, buttonProps }) => (
                    <UdirPagination.Item key={itemKey}>
                        {typeof page === 'number' ? (
                            <UdirPagination.Button {...buttonProps} aria-label={`Side ${page}`}>
                                {page}
                            </UdirPagination.Button>
                        ) : (
                            <span aria-hidden="true">...</span>
                        )}
                    </UdirPagination.Item>
                ))}
                <UdirPagination.Item>
                    <UdirPagination.Button {...nextButtonProps}>
                        {!isMobile && 'Neste'}
                    </UdirPagination.Button>
                </UdirPagination.Item>
            </UdirPagination.List>
        </UdirPagination>
    );
}

export default Pagination;
